import { MapPin, Globe, Instagram } from "lucide-react";
import React, { useState } from "react";
import { VenueCarousel } from "./venues/VenueCarousel";
import { ShortlistedVenueCarousel } from "./venues/ShortlistedVenueCarousel";
import { SuburbCombobox } from "../create-event/form/location/SuburbCombobox";
import { useEventDetails } from "@/hooks/use-event-details";
import { useParams } from "react-router-dom";
import { Separator } from "../ui/separator";
import { VenueList } from "../create-event/form/location/VenueList";
import { useVenueDetails } from "@/hooks/use-venue-details";
import { cn } from "@/lib/utils";

interface VenueDetails {
  name: string;
  address: string;
  venue_id?: string;
  image_url?: string | null;
  style?: string | null;
  price_range?: string | null;
  website_url?: string | null;
  instagram_handle?: string | null;
  google_maps_link?: string | null;
}

interface EventLocationProps {
  status?: string;
  locationType?: 'specific' | 'flexible';
  flexibleLocationDescription?: string;
  preferredVenues?: string[];
  selectedVenue?: VenueDetails | null;
  eventId?: string;
  isHost?: boolean;
}

export const EventLocation = ({
  status,
  locationType,
  flexibleLocationDescription,
  preferredVenues = [],
  selectedVenue,
  eventId,
  isHost = false
}: EventLocationProps) => {
  const {
    eventId: routeEventId
  } = useParams();
  const currentEventId = eventId || routeEventId || '';
  const [selectedSuburbs, setSelectedSuburbs] = useState<string[]>(preferredVenues || []);
  const {
    updateMutation
  } = useEventDetails(currentEventId);
  
  const { data: detailedVenue } = useVenueDetails(currentEventId);

  const handleSuburbSelect = (suburb: string) => {
    if (!selectedSuburbs.includes(suburb)) {
      const newSuburbs = [...selectedSuburbs, suburb];
      setSelectedSuburbs(newSuburbs);

      if (isHost) {
        updateMutation.mutate({
          preferred_venues: newSuburbs
        });
      }
    }
  };

  const handleRemoveSuburb = (suburb: string) => {
    const newSuburbs = selectedSuburbs.filter(s => s !== suburb);
    setSelectedSuburbs(newSuburbs);

    if (isHost) {
      updateMutation.mutate({
        preferred_venues: newSuburbs
      });
    }
  };

  if ((status === 'confirmed' || status === 'booked') && (selectedVenue || detailedVenue)) {
    const venue: VenueDetails = detailedVenue || { 
      name: selectedVenue?.name || 'Venue', 
      address: selectedVenue?.address || 'Address',
      image_url: selectedVenue?.image_url || null,
      style: selectedVenue?.style || null,
      price_range: selectedVenue?.price_range || null,
      website_url: selectedVenue?.website_url || null,
      instagram_handle: selectedVenue?.instagram_handle || null,
      google_maps_link: selectedVenue?.google_maps_link || null
    };
    
    return (
      <div className="space-y-4">
        <div className="border rounded-lg overflow-hidden bg-white">
          {venue.image_url ? (
            <div 
              className="h-[160px] w-full bg-cover bg-center"
              style={{ backgroundImage: `url(${venue.image_url})` }}
              aria-label="Venue image"
            />
          ) : (
            <div 
              className="h-[160px] w-full bg-gradient-to-br from-[#E5DEFF] to-[#9b87f5]"
              aria-label="Venue image placeholder"
            />
          )}
          
          <div className="p-4 space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <h3 className="font-semibold">{venue.name}</h3>
                <p className="text-sm text-gray-600">{venue.address}</p>
                
                {(venue.style || venue.price_range) && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mt-2">
                    {venue.style && (
                      <span className="px-2 py-1 bg-secondary rounded-full">{venue.style}</span>
                    )}
                    {venue.price_range && (
                      <span className="px-2 py-1 bg-secondary rounded-full">{venue.price_range}</span>
                    )}
                  </div>
                )}
                
                <div className="flex items-center gap-3 mt-3">
                  {venue.website_url && (
                    <a 
                      href={venue.website_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
                    >
                      <Globe size={16} />
                    </a>
                  )}
                  
                  {venue.instagram_handle && (
                    <a 
                      href={`https://instagram.com/${venue.instagram_handle.replace('@', '')}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
                    >
                      <Instagram size={16} />
                    </a>
                  )}
                  
                  {venue.google_maps_link && (
                    <a 
                      href={venue.google_maps_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:text-primary/80 transition-colors flex items-center gap-1"
                    >
                      Directions
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (locationType === 'flexible' && flexibleLocationDescription) {
    return (
      <div className="space-y-2">
        <div className="flex items-start gap-3">
          <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Flexible Location</p>
            <p className="text-sm text-gray-600">{flexibleLocationDescription}</p>
          </div>
        </div>
      </div>
    );
  }

  const showVenueCarousels = status === 'planned' && locationType === 'specific' && selectedSuburbs.length > 0 && currentEventId && isHost;
  const showSuburbSelection = isHost && status === 'planned' && locationType === 'specific';
  const showSuburbList = status === 'planned' && locationType === 'specific' && selectedSuburbs.length > 0;

  return <div className="space-y-4">
      {showVenueCarousels && (
        <>
          <ShortlistedVenueCarousel eventId={currentEventId} />
          
          <div>
            <VenueCarousel eventId={currentEventId} suburbs={selectedSuburbs} />
          </div>
        </>
      )}
      
      {showVenueCarousels && showSuburbSelection && <Separator className="my-6" />}
      
      {showSuburbList && !isHost && (
        <div className="mt-4">
          <VenueList venues={selectedSuburbs} onRemoveVenue={() => {}} label="Preferred Locations" />
        </div>
      )}

      {showSuburbSelection && <div className="mt-4">
          <SuburbCombobox onSuburbSelect={handleSuburbSelect} selectedSuburbs={selectedSuburbs} />
          {selectedSuburbs.length > 0 && <div className="mt-2 flex flex-wrap gap-2">
              {selectedSuburbs.map(suburb => <div key={suburb} className="bg-secondary text-secondary-foreground px-2 py-1 rounded-md text-sm flex items-center gap-1">
                  {suburb}
                  <button onClick={() => handleRemoveSuburb(suburb)} className="ml-1 text-secondary-foreground/70 hover:text-secondary-foreground">
                    ×
                  </button>
                </div>)}
            </div>}
        </div>}
    </div>;
};
